import { inject, Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { HttpClient, HttpHeaders, httpResource } from '@angular/common/http';
import { Log, Tool, ToolRegistration } from '../../../utils/models/tool.models';

/**
 * Service for interacting with tools.
 */
@Injectable({
  providedIn: 'root',
})
export class ToolService {
  /** Inject services. */
  private readonly httpService = inject(HttpClient);

  getAllToolsAdmin() {
    return httpResource<Tool[]>(() => 'internal/tools');
  }

  getToolConfigById(idSignal: () => number | undefined) {
    return httpResource<Tool>(() => {
      const id = idSignal();
      if (id) return `external/tools/${id}`;
      return undefined;
    });
  }

  getRegistrations() {
    return httpResource<ToolRegistration[]>(() => `internal/tools/registrations`);
  }

  updateRegistration(userId: number, toolId: number) {
    const payload = { registrations: [{ user_id: userId, tool_id: toolId }] };
    const res = this.httpService.post<string>(`internal/tools/registrations`, payload);
    return firstValueFrom(res);
  }

  deleteRegistration(userId: number, toolId: number) {
    const payload = { registrations: [{ user_id: userId, tool_id: toolId }] };
    const options = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
      body: payload,
    };
    const res = this.httpService.delete<string>(`internal/tools/registrations`, options);
    return firstValueFrom(res);
  }

  uploadTool(toolInputData: FormData) {
    const res = this.httpService.post<Log>('internal/tools', toolInputData);
    return firstValueFrom(res);
  }

  getToolInstallationLogs(toolId: () => number | undefined) {
    return httpResource<Log>(() => {
      const id = toolId();
      if (id) return `internal/tools/${id}/logs`;
      return undefined;
    });
  }

  triggerToolReinstallation(toolId: number) {
    const res = this.httpService.post(`internal/tools/${toolId}`, {});
    return firstValueFrom(res);
  }
}
